import {Inject, Injectable} from '@angular/core';
import {DOCUMENT} from "@angular/common";
import {Observable, Subject} from "rxjs";
import {environment} from "../../../../environments/environment";
import {CordovaService, PLATFORM} from "../cordova/cordova.service";
import {INAPPBROWSER_CLOSE_BUTTON_CAPTION, INAPPBROWSER_TOOLBAR_COLOR} from "../../defines/client.const";
import {isValidNaverKey} from "../../defines/naver-key.func";

import $ from 'jquery';

declare global {
  var naver_id_login: any;
}

export const NAVER_CALLBACK_SIGN = 'naver_callback';

@Injectable({
  providedIn: 'root'
})
export class NaverLoginService {
  private naverLoginInstance: any;
  private isLoginWindowOpen: boolean = false;

  // 네이버 로그인 관련
  private readonly NAVER_CLIENT_ID: string = environment.naverClientId;
  private readonly NAVER_CALLBACK_URL: string = environment.naverCallbackUrl;

  private userProfile$: Subject<string> = new Subject<string>();
  public get userProfile(): Observable<string> {
    return this.userProfile$.asObservable();
  }

  private login$: Subject<string> = new Subject<string>();
  public get login(): Observable<string> {
    return this.login$.asObservable();
  }

  private loginError$: Subject<{detail: string; value: unknown}> = new Subject();
  public get loginError(): Observable<{detail: string; value: unknown}> {
    return this.loginError$.asObservable();
  }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private cordovaSvc: CordovaService,
  ) {
    (window as any).$ = $;

    if (this.cordovaSvc.isAndroid) {
      this.NAVER_CALLBACK_URL = environment.naverCallbackUrlAndroid;
    } else if (this.cordovaSvc.isIOS) {
      this.NAVER_CALLBACK_URL = environment.naverCallbackUrlIOS;
    }

    this.naverLoginInstance = new naver_id_login(this.NAVER_CLIENT_ID, this.NAVER_CALLBACK_URL);

    (window as any).naverSignInCallback = this.naverSignInCallback.bind(this);


    /**
     * 메시지 수신 처리
     */
    (window as any).addEventListener('message', this.cbMessage.bind(this));
  }

  private cbMessage(event: any): void {

    if (event.data?.origin === NAVER_CALLBACK_SIGN) {
      console.warn("cbMessage()", event.data, event.source);

      if (!isValidNaverKey(event.data.naverKey)) {
        console.error("cbMessage() invalid naverKey", event.data.naverKey);
        this.loginError$.next({detail: 'invalid naverKey', value: event.data.naverKey});
        return;
      }

      const naverKey = event.data.naverKey.trim();

      this.login$.next(naverKey);
    }
  }

  /**
   * Naver 콜백을 받는 함수. naver id의 key값을 얻는다
   * 주의 앱에서는 사용하지 않는다. 따라서 이 함수는 사용하지 않음
   * callback 쪽 루틴에서 get_naver_userprofile() 함수를 직접 호출한다.
   */
  public GetUserProfile(): Observable<string> {
    console.log("GetUserProfile()");

    this.naverLoginInstance.get_naver_userprofile("naverSignInCallback()");

    return this.userProfile$.asObservable();
  }


  /**
   * 템플릿에 지정한 <div id="naver_id_login"> 요소에 네이버 디자인과 버튼 기능을 추가하는 함수.
   */
  public MakeLoginBtn(): void {
    const state = this.naverLoginInstance.getUniqState();
    this.naverLoginInstance.setButton("green", 2, 43);
    this.naverLoginInstance.setState(state); // 넣기는 하지만 사용하지 않음.
    // this.naverLoginInstance.setDomain(environment.host); // same subdomain으로 필요하지 않음.
    // this.naverLoginInstance.setPopup(); // 설졍하면 팝업창이 열리면서 로그인 한다.

    this.naverLoginInstance.init_naver_id_login();
  }

  private naverSignInCallback(): void {
    console.warn("naverSignInCallback()");

    const id = this.naverLoginInstance.getProfileData('id');

    this.userProfile$.next(id);
  }

  /**
   * init_naver_id_login() 함수로 만들어진 네이버 버튼에 속성을 바꿔서,
   * InAppBrowser로 네이버 로그인 창이 뜨도록 변경하는 함수
   */
  public CustomizeNaverBtn() {
    const naver_link = this.document.querySelector('#naver_id_login a');
    const naver_url: string = naver_link.getAttribute('href');
    const button_img = this.document.querySelector('#naver_id_login a img');
    if (button_img.getAttribute('src').indexOf('https') == -1) {
      button_img.setAttribute('src', button_img.getAttribute('src').replace('http', 'https'));
    }
    naver_link.removeAttribute('href');
    naver_link.removeAttribute('onclick');
    naver_link.addEventListener('click', (event) => {
      if (this.isLoginWindowOpen) {
        return;
      }

      if (this.cordovaSvc.cordova) {
        const options = [
          'location=no',
          'fullscreen=yes',
          'toolbartranslucent=no',
          `toolbarcolor=${INAPPBROWSER_TOOLBAR_COLOR}`,
          `closebuttoncaption=${INAPPBROWSER_CLOSE_BUTTON_CAPTION}`,
          'hardwareback=yes',
          'hidenavigationbuttons=yes',
        ].join(',');

        this.isLoginWindowOpen = true;

        let win: any;
        try {
          win = this.cordovaSvc.cordova.InAppBrowser.open(naver_url, '_blank', options);
        } catch (error) {
          this.isLoginWindowOpen = false;
          console.warn('CustomizeNaverBtn() InAppBrowser.open threw', error);
          return;
        }

        if (!win) {
          this.isLoginWindowOpen = false;
          console.warn('CustomizeNaverBtn() InAppBrowser.open returned no window');
          return;
        }

        const callbackLoadStop = (loadStopEvent: any) => {
          try {
            const currentUrl = loadStopEvent?.url ?? '';
            if (!currentUrl.includes('nid.naver.com')) {
              return;
            }

            // Android InAppBrowser: callback 지정 시 prompt 기반 wrapping 경로로 가서 네이버 WebView DOM 변경이 반영되지 않음.
            win.executeScript({
              code: `
              (function() {
                try {
                  var selectors = ['#back', '[aria-label*="뒤로"]', 'button[aria-label*="back"]'];
                  var backButton = null;
                  for (var i = 0; i < selectors.length; i += 1) {
                    backButton = document.querySelector(selectors[i]);
                    if (backButton) {
                      break;
                    }
                  }

                  if (!backButton) {
                    console.warn('naver-login: back button selector miss', {
                      href: location.href,
                      triedSelectors: selectors,
                    });
                    return;
                  }

                  if (backButton.getAttribute('data-iab-back-bound') === '1') {
                    return;
                  }

                  backButton.setAttribute('data-iab-back-bound', '1');
                  backButton.addEventListener('click', function(event) {
                    event.preventDefault();
                    event.stopPropagation();

                    var payload = JSON.stringify({ type: 'naver-login-close' });
                    try {
                      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.cordova_iab) {
                        window.webkit.messageHandlers.cordova_iab.postMessage(payload);
                        return;
                      }
                    } catch (error) {
                      console.warn('naver-login back: webkit.cordova_iab postMessage failed', error);
                    }

                    try {
                      if (window.cordova_iab && typeof window.cordova_iab.postMessage === 'function') {
                        window.cordova_iab.postMessage(payload);
                        return;
                      }
                    } catch (error) {
                      console.warn('naver-login back: cordova_iab.postMessage failed', error);
                    }

                    console.warn('naver-login back: no iab transport available, falling back to window.postMessage');
                    window.postMessage({ type: 'naver-login-close' }, '*');
                  }, true);
                } catch (error) {
                  console.warn('naver-login: back button binding failed', error);
                }
              })();
            `
            });
          } catch (error) {
            console.warn('CustomizeNaverBtn() loadstop executeScript failed', {url: loadStopEvent?.url, error});
          }
        };

        /**
         * 위의 InAppBrowser에서 보낸 메시지를 수신하는 루틴
         * win 이 Cordova InAppBrowser 객체라는 것에 주의!
         */
        win.addEventListener('message', (event) => {
          if (event?.data?.type === 'naver-login-close') {
            try {
              win.close();
            } finally {
              this.isLoginWindowOpen = false;
            }
            return;
          }

          const naverKey = event['data']?.id;
          if (!isValidNaverKey(naverKey)) {
            console.warn('CustomizeNaverBtn() invalid naverKey message', event?.data);
            try {
              win.close();
            } catch (error) {
              console.warn('CustomizeNaverBtn() win.close after invalid key failed', error);
            } finally {
              this.isLoginWindowOpen = false;
            }
            this.loginError$.next({detail: 'invalid naverKey', value: event?.data?.id});
            return;
          }
          // window.alert(`win id = ${id}`);

          /**
           * 다시 전체 윈도우에 메시지를 보낸다.
           */
          const message = {
            naverKey: naverKey.trim(),
            origin: NAVER_CALLBACK_SIGN,
          }
          window.postMessage(message, '*');
          setTimeout(() => {
            try {
              win.close();
            } finally {
              this.isLoginWindowOpen = false;
            }
          }, 500);
        });
        win.addEventListener('loadstop', callbackLoadStop);
        win.addEventListener('exit', () => {
          this.isLoginWindowOpen = false;
        });

      } else {
        this.isLoginWindowOpen = true;
        let popup: Window | null = null;
        try {
          // location.replace(naver_url);
          popup = window.open(naver_url);
        } catch (error) {
          this.isLoginWindowOpen = false;
          console.warn('CustomizeNaverBtn() window.open threw', error);
          return;
        }

        if (!popup) {
          this.isLoginWindowOpen = false;
          console.warn('CustomizeNaverBtn() window.open returned no window (popup blocked?)');
          return;
        }

        // 새 탭이 열린 직후의 빠른 중복 클릭을 막기 위한 단순 시간 가드.
        // 웹은 InAppBrowser의 exit 같은 닫힘 신호가 없어 시간 기반으로만 해제.
        setTimeout(() => {
          this.isLoginWindowOpen = false;
        }, 1000);
      }
    });
  }
}
